import type React from "react";

import { PlainTooltip } from "@similarweb/ui-components";

import type { TranslateFunction } from "app/@types/I18nInterfaces";
import {
    ACCOUNTS_AND_CONTACTS_MANDATORY_FIELDS,
    ACCOUNTS_MANDATORY_FIELDS,
    CONTACTS_MANDATORY_FIELDS,
} from "app/pages/sales-intelligence/sub-modules/management/constants";
import type { ExportName } from "app/pages/sales-intelligence/sub-modules/management/store/type";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import { getConfigByExportName } from "app/pages/sales-intelligence/sub-modules/management/utils/utils";
import { StyledTableHeaderCell } from "app/pages/workspace/sales/components/custom-table/TableCell/styles";
import type { TableCell } from "app/pages/workspace/sales/components/custom-table/types";

import { ADD_FIELD } from "app/pages/sales-intelligence/common-components/export-table/const";
import type { ExportItem } from "app/pages/sales-intelligence/sub-modules/management/store/type";

type TableColumns = {
    id: TableCell;
    title: TableCell;
    example: TableCell;
    externalField: TableCell;
    overwrite: TableCell;
    field: TableCell;
    delete: TableCell;
};

type SelectedFieldTableColumns = {
    similarweb: TableCell;
    selected: TableCell;
};

type ColumnKey =
    | "id"
    | "field"
    | "title"
    | "example"
    | "externalField"
    | "overwrite"
    | "delete"
    | "forExport"
    | "forEnrichment";

export const createExportTableRenderer = (
    translate: TranslateFunction,
    integrationName: Integration,
    exportName: ExportName,
) => {
    const fieldsConfig = getConfigByExportName(integrationName, exportName);

    const currentIntegrationField = {
        headers: {
            hubspot: {
                externalField: {
                    tooltip: translate("si.management.hs.settings_export.settings.title.column.hubspot.tooltip"),
                    title: translate("si.management.hs.settings_export.settings.title.column.hubspot"),
                },
                overwrite: {
                    tooltip: translate(
                        "si.management.sf.settings_export.settings.title.column.overview.hubspot.tooltip",
                    ),
                    title: translate("si.management.sf.settings_export.settings.title.column.overview"),
                },
            },
            salesforce: {
                externalField: {
                    tooltip: translate("si.management.sf.settings_export.settings.title.column.salesforce.tooltip"),
                    title: translate("si.management.sf.settings_export.settings.title.column.salesforce"),
                },
                overwrite: {
                    tooltip: translate(
                        "si.management.sf.settings_export.settings.title.column.overview.salesforce.tooltip",
                    ),
                    title: translate("si.management.sf.settings_export.settings.title.column.overview"),
                },
            },
            dynamics: {
                externalField: {
                    tooltip: translate("si.management.dynamics.settings_export.settings.title.column.dynamics.tooltip"),
                    title: translate("si.management.dynamics.settings_export.settings.title.column.dynamics"),
                },
                overwrite: {
                    tooltip: translate("si.management.dynamics.settings_export.settings.title.column.overview.tooltip"),
                    title: translate("si.management.dynamics.settings_export.settings.title.column.overview"),
                },
            },
        },
        columns: {
            hubspot: {
                externalField: {
                    text: translate("si.management.hs.settings_export.settings.title.column.hubspot"),
                },
            },
            salesforce: {
                externalField: {
                    text: translate("si.management.sf.settings_export.settings.title.column.salesforce"),
                },
            },
            dynamics: {
                externalField: {
                    text: translate("si.management.dynamics.settings_export.settings.title.column.dynamics"),
                },
            },
        },
    };

    const headersConfig = {
        title: {
            tooltip: translate("si.management.sf.settings_export.settings.title.column.similarweb.tooltip"),
            title: translate("si.management.sf.settings_export.settings.title.column.similarweb"),
        },
        example: {
            tooltip: translate("si.management.sf.settings_export.settings.title.column.example.tooltip"),
            title: translate("si.management.sf.settings_export.settings.title.column.example"),
        },

        externalField: {
            ...currentIntegrationField.headers[integrationName].externalField,
        },
        forExport: {
            tooltip: "",
            title: translate("si.management.hs.settings_export.settings.title.column.export"),
        },
        forEnrichment: {
            tooltip: "",
            title: translate("si.management.hs.settings_export.settings.title.column.enrichment"),
        },
        overwrite: {
            ...currentIntegrationField.headers[integrationName].overwrite,
        },
        delete: {
            tooltip: "",
            title: "",
        },
    };

    return {
        getColumns({ ignoredColumns = [] }: { ignoredColumns?: ColumnKey[] } = {}): Readonly<TableColumns> {
            const allColumns = {
                id: { align: "left", text: "", size: "0%" },
                field: { align: "left", text: "", size: "0%" },
                title: {
                    align: "left",
                    text: translate("si.management.sf.settings_export.settings.title.column.similarweb"),
                    size: "23%",
                },
                example: {
                    align: "left",
                    text: translate("si.management.sf.settings_export.settings.title.column.example"),
                    size: "23%",
                },
                externalField: {
                    align: "left",
                    size: "23%",
                    ...currentIntegrationField.columns[integrationName].externalField,
                },
                forExport: {
                    align: "left",
                    text: translate("si.management.sf.settings_export.settings.title.column.overview"),
                    size: "7%",
                },
                forEnrichment: {
                    align: "left",
                    text: translate("si.management.sf.settings_export.settings.title.column.overview"),
                    size: "9%",
                },
                overwrite: {
                    align: "left",
                    text: translate("si.management.sf.settings_export.settings.title.column.overview"),
                    size: "8%",
                },
                delete: { align: "center", text: "", size: "7%" },
            };

            let totalIgnoredSize = 0;
            ignoredColumns.forEach((column) => {
                if (allColumns[column]) {
                    totalIgnoredSize += parseFloat(allColumns[column].size);
                    delete allColumns[column];
                }
            });

            const totalRemainingSize = 100 - totalIgnoredSize;

            Object.keys(allColumns).forEach((key) => {
                const column = allColumns[key as keyof typeof allColumns];
                const originalSize = parseFloat(column.size);
                const newSize = (originalSize / totalRemainingSize) * 100;
                column.size = newSize.toFixed(2) + "%";
            });

            return allColumns;
        },
        getSettingsColumns(): Readonly<SelectedFieldTableColumns> {
            return {
                similarweb: {
                    align: "left",
                    text: translate("si.management.sf.settings_export.settings.title.column.similarweb"),
                    size: "70%",
                },
                selected: {
                    align: "left",
                    text: "",
                    size: "30%",
                },
            };
        },
        renderCustomHeaderCell(key: string, total?: number): () => React.ReactNode {
            const { title, tooltip } = headersConfig[key as keyof typeof headersConfig];

            return () => {
                return (
                    <PlainTooltip
                        tooltipContent={tooltip}
                        enabled={!!tooltip}
                    >
                        <div>
                            <StyledTableHeaderCell>
                                {title}
                                {total !== undefined ? ` (${total - 1})` : null}
                            </StyledTableHeaderCell>
                        </div>
                    </PlainTooltip>
                );
            };
        },
        isRequiredField(field: string): boolean {
            if (exportName === "companyAsLeads") {
                return false;
            }

            let fields: readonly string[];

            if (exportName === "contacts") {
                fields =
                    integrationName === "salesforce" || integrationName === "dynamics"
                        ? CONTACTS_MANDATORY_FIELDS
                        : ACCOUNTS_AND_CONTACTS_MANDATORY_FIELDS;
            } else {
                fields = ACCOUNTS_MANDATORY_FIELDS;
            }

            return fields.some((requiredField) => requiredField === field);
        },
        isPrimaryRequiredFieldInstance(setting: ExportItem, tableData: readonly ExportItem[]): boolean {
            if (exportName === "companyAsLeads") {
                return false;
            }

            const metadata = setting.metadata ?? "";

            if (!metadata || !this.isRequiredField(metadata)) {
                return false;
            }

            const firstIndex = tableData.findIndex((row) => row.field !== ADD_FIELD && row.metadata === metadata);
            const settingIndex = tableData.indexOf(setting);

            return firstIndex >= 0 && firstIndex === settingIndex;
        },
        getOverwriteCellTooltipContent(overwrite: boolean): string {
            return overwrite
                ? translate(
                      `si.management.sf.settings_export.settings.title.column.overview.${integrationName}.enabled_toggle.tooltip`,
                  )
                : translate(
                      `si.management.sf.settings_export.settings.title.column.overview.${integrationName}.disabled_toggle.tooltip`,
                  );
        },
    };
};
